import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Badge,
  Spinner,
} from 'reactstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { getAllActivePlans } from '../../../../api';
import convertToRupees from '../../../../utilities/convertToRupees';

/**
 * RecordDetailsModal - React Component for displaying record details modal
 * Handles addon fetching, document details, discount info, and addon dropdowns
 */

const RecordDetailsModal = ({
  isOpen,
  toggle,
  item,
  recordWithDiscount,
  dataSource,
  handleAddonSelect,
  removeAddon,
  calculateYears,
  selectedServices,
  additionalCost,
}) => {
  const [assistanceAddons, setAssistanceAddons] = useState([]);
  const [insuranceAddons, setInsuranceAddons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlanDetails, setSelectedPlanDetails] = useState({});
  const [localAssistanceAddon, setLocalAssistanceAddon] = useState(null);
  const [localInsuranceAddon, setLocalInsuranceAddon] = useState(null);
  // Local mirror of addon state so changes are reflected immediately without waiting
  // for the parent to propagate updated props back down.
  const [localServices, setLocalServices] = useState(null);   // null = use props
  const [localAddonCost, setLocalAddonCost] = useState(null); // null = use props

  // Fetch addons for this record when modal opens
  useEffect(() => {
    if (!isOpen || !item) return;
    // Reset local mirror so we start fresh from props each time the modal opens
    setLocalServices(null);
    setLocalAddonCost(null);
    setLocalAssistanceAddon(null);
    setLocalInsuranceAddon(null);

    const fetchAddons = async () => {
      try {
        setLoading(true);
        const docDetails = item.documentDetails;
        const traveller_dob = item.travellers.map(traveller => traveller.traveller_dob);
        const journey_start_date = docDetails?.travel_start_date;
        const years = calculateYears(item.travellers?.[0]?.traveller_dob, docDetails?.travel_start_date);

        const addonsData = {
          journey_start_date: journey_start_date,
          typeId: item?.type_id || Number(dataSource?.partnership?.type_id),
          geographicalCoverId: item?.geographical_cover_id || Number(dataSource?.partnership?.geographical_cover_id),
          age: years,
          planId: item?.planDetails?.plan_id || null,
          travelDays: Number(docDetails?.number_of_travel_days || 0),
          companyId: docDetails?.company_id || Number(dataSource?.partnership?.company_id),
          ped: item.ped || null,
          pageSize: 1,
          limit: 100,
          traveller_dob: traveller_dob,
          partnership_id: dataSource?.partnership?.partnership_id || null,
        };

        const response = await getAllActivePlans(addonsData);

        const initialSelectedDetails = {};
        const plansWithCosts = response?.data?.result?.fetchPlan || [];
        plansWithCosts.forEach((plan) => {
          if (plan.plan_costs.length > 0) {
            initialSelectedDetails[plan.id] = {
              cover: plan.plan_costs[0].cover,
              cost: plan.plan_costs[0].cost,
              coi: plan.plan_costs[0].coi,
            };
          }
        });
        setSelectedPlanDetails(initialSelectedDetails);

        if (plansWithCosts.length > 0) {
          const parsedAssistance = [];
          const parsedInsurance = [];
          plansWithCosts.forEach((plan) => {
            const addons = plan && plan.plan_has_addons ? plan.plan_has_addons : [];
            addons.forEach((p) => {
              const addonType = p.add_on_type?.type?.toLowerCase();
              const addonObj = {
                value: p.service.id,
                label: p.service.title,
                percentage: p.service.percentage,
                addon_coa_value: p?.addon_coa_value,
                addon_coi_value: p?.addon_coi_value,
                addon_value_type: p?.addon_value_type,
                add_on_type: p?.add_on_type?.type,
              };
              if (addonType === 'assistance') {
                // Avoid duplicates
                if (!parsedAssistance.find(a => a.value === addonObj.value)) {
                  parsedAssistance.push(addonObj);
                }
              } else if (addonType === 'insurance') {
                if (!parsedInsurance.find(a => a.value === addonObj.value)) {
                  parsedInsurance.push(addonObj);
                }
              }
            });
          });
          setAssistanceAddons(parsedAssistance);
          setInsuranceAddons(parsedInsurance);
        } else {
          setAssistanceAddons([]);
          setInsuranceAddons([]);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching addons:', error);
        setAssistanceAddons([]);
        setInsuranceAddons([]);
        setLoading(false);
      }
    };

    fetchAddons();
  }, [isOpen, item]);

  const calculateAddonCost = (addon, basePrem) => {
    if (!addon) return 0;
    if (addon.addon_value_type?.toLowerCase() === 'fixed') {
      return Number(addon.addon_coa_value) || 0;
    }
    // Always use addon_coa_value as the percentage — it is the plan-level override.
    // service.percentage is a service-level default and must NOT take priority here.
    const percentage = Number(addon.addon_coa_value) || 0;
    return (basePrem * percentage) / 100;
  };

  if (!item) return null;

  const docDetails = item.documentDetails;
  const recordId = item.id || item.proposer?.proposer_id || item.record_index;
  console.log("[RecordDetailsModal] item.id:", item.id, "| item.proposer?.proposer_id:", item.proposer?.proposer_id, "| item.record_index:", item.record_index, "→ recordId:", recordId);

  const hasDiscount = recordWithDiscount && recordWithDiscount.discount;
  const discountInfo = hasDiscount ? recordWithDiscount.discount : null;
  const baseBasePremium = docDetails?.base_premium || 0;
  const finalBasePremium = hasDiscount ? discountInfo.finalBaseAmount : baseBasePremium;

  // Resolve the raw plan cost from the fetched plan details (matches generate-quote logic)
  // plan_costs[0].cost is the base for percentage-type addons, NOT base_premium
  const planId = item?.planDetails?.plan_id;
  const planCostForAddon = selectedPlanDetails[planId]?.cost || finalBasePremium;
  const planCOICostForAddon = selectedPlanDetails[planId]?.coi || 0;

  // Get already-selected addons for this record — prefer local mirror so changes
  // are reflected immediately without waiting for parent props to propagate.
  const existingServices = localServices !== null
    ? localServices
    : ((selectedServices && selectedServices[recordId]) || []);
  const recordAddonCost = localAddonCost !== null
    ? localAddonCost
    : ((additionalCost && additionalCost[recordId]) || 0);

  // Live pricing calculation in modal — use planCostForAddon as base for percentage addons
  const assistanceCost = localAssistanceAddon ? calculateAddonCost(localAssistanceAddon, planCostForAddon) : 0;
  const insuranceCost = localInsuranceAddon ? calculateAddonCost(localInsuranceAddon, planCostForAddon) : 0;
  const liveTotalAddonCost = assistanceCost + insuranceCost + recordAddonCost;
  const liveBasePlusAddon = finalBasePremium + liveTotalAddonCost;
  const liveGst = liveBasePlusAddon * 0.18;
  const liveMemberPremium = liveBasePlusAddon + liveGst;

  // Recompute discount section values using live addon cost so they update on the spot
  const liveDiscountTotalAmount = hasDiscount
    ? Number((finalBasePremium + liveTotalAddonCost).toFixed(2))
    : 0;
  const liveDiscountTaxAmount = hasDiscount
    ? Number((liveDiscountTotalAmount * 0.18).toFixed(2))
    : 0;
  const liveDiscountFinalPayable = hasDiscount
    ? Number((liveDiscountTotalAmount + liveDiscountTaxAmount).toFixed(2))
    : 0;

  // Format addon option labels
  const formatAddonOptions = (addons) =>
    addons.map((a) => {
      const costDisplay =
        a.addon_value_type?.toLowerCase() === 'fixed'
          ? `₹${a.addon_coa_value}`
          : `${a.addon_coa_value}%`;
      return { ...a, label: `${a.label} (${costDisplay})` };
    });

  const handleAssistanceSelect = (selectedOption) => {
    setLocalAssistanceAddon(selectedOption);
  };

  const handleInsuranceSelect = (selectedOption) => {
    setLocalInsuranceAddon(selectedOption);
  };

  const handleAddAssistance = () => {
    if (!localAssistanceAddon) {
      toast.warn('Please select an Assistance addon first.');
      return;
    }
    // Update local mirror immediately so the modal re-renders without waiting for props
    const addonCostAmt = calculateAddonCost(localAssistanceAddon, planCostForAddon);
    setLocalServices(prev => {
      const base = prev !== null ? prev : ((selectedServices && selectedServices[recordId]) || []);
      return [...base, localAssistanceAddon];
    });
    setLocalAddonCost(prev => {
      const base = prev !== null ? prev : ((additionalCost && additionalCost[recordId]) || 0);
      return base + addonCostAmt;
    });
    handleAddonSelect(localAssistanceAddon, recordId, planCostForAddon, planCOICostForAddon);
    setLocalAssistanceAddon(null);
  };

  const handleAddInsurance = () => {
    if (!localInsuranceAddon) {
      toast.warn('Please select an Insurance addon first.');
      return;
    }
    const addonCostAmt = calculateAddonCost(localInsuranceAddon, planCostForAddon);
    setLocalServices(prev => {
      const base = prev !== null ? prev : ((selectedServices && selectedServices[recordId]) || []);
      return [...base, localInsuranceAddon];
    });
    setLocalAddonCost(prev => {
      const base = prev !== null ? prev : ((additionalCost && additionalCost[recordId]) || 0);
      return base + addonCostAmt;
    });
    handleAddonSelect(localInsuranceAddon, recordId, planCostForAddon, planCOICostForAddon, 'insurance');
    setLocalInsuranceAddon(null);
  };

  const handleRemoveAddon = (svc, rId) => {
    const addonCostAmt = calculateAddonCost(svc, planCostForAddon);
    setLocalServices(prev => {
      const base = prev !== null ? prev : ((selectedServices && selectedServices[recordId]) || []);
      return base.filter(s => s.value !== svc.value);
    });
    setLocalAddonCost(prev => {
      const base = prev !== null ? prev : ((additionalCost && additionalCost[recordId]) || 0);
      return Math.max(0, base - addonCostAmt);
    });
    removeAddon && removeAddon(svc, rId);
  };

  const selectStyles = {
    control: (base) => ({
      ...base,
      fontSize: '11px',
      minHeight: '34px',
      backgroundColor: '#e0e4f3',
      borderColor: '#ddd',
      fontFamily: "'Poppins', sans-serif",
    }),
    option: (base) => ({ ...base, fontSize: '11px', fontFamily: "'Poppins', sans-serif" }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      scrollable
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <ModalHeader toggle={toggle} style={{ fontSize: '14px', fontWeight: '600', color: '#00239c' }}>
        Record Details
        {item.travellers?.[0] && (
          <span style={{ fontSize: '12px', fontWeight: '400', color: '#666', marginLeft: '10px' }}>
            — {item.travellers[0]?.title || ''} {item.travellers[0]?.firstName || ''} {item.travellers[0]?.lastName || ''}
          </span>
        )}
      </ModalHeader>

      <ModalBody style={{ fontSize: '12px', maxHeight: '70vh', overflowY: 'auto' }}>
        {/* Document Details */}
        <h6 style={{ color: '#333', marginTop: '4px', marginBottom: '8px', fontSize: '12px' }}>
          <strong>Document Details</strong>
        </h6>
        <Table bordered size="sm" style={{ fontSize: '11px', marginBottom: '16px' }}>
          <tbody>
            <tr>
              <td style={{ width: '45%', fontWeight: '600' }}>Travel Start</td>
              <td>{docDetails?.travel_start_date ? new Date(docDetails.travel_start_date).toLocaleDateString() : '-'}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: '600' }}>Travel End</td>
              <td>{docDetails?.travel_end_date ? new Date(docDetails.travel_end_date).toLocaleDateString() : '-'}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: '600' }}>Days</td>
              <td>{docDetails?.number_of_travel_days}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: '600' }}>Base Fees</td>
              <td>{convertToRupees(finalBasePremium)}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: '600' }}>Coverage</td>
              <td>$ {docDetails?.member_coverage?.toLocaleString()}</td>
            </tr>
            {/* <tr>
              <td style={{ fontWeight: '600' }}>Plan</td>
              <td>{item?.planDetails?.title || '-'}</td>
            </tr> */}
          </tbody>
        </Table>

        {/* Discount Details */}
        {discountInfo && (
          <>
            <h6 style={{ color: '#28a745', marginTop: '12px', marginBottom: '8px', fontSize: '12px' }}>
              <strong>✓ Discount Applied</strong>
            </h6>
            <Table bordered size="sm" style={{ fontSize: '11px', marginBottom: '16px', backgroundColor: '#f0f8f0' }}>
              <tbody>
                <tr>
                  <td style={{ width: '55%', fontWeight: '600' }}>Discount Percentage</td>
                  <td style={{ textAlign: 'right' }}>{discountInfo.percentage}%</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: '600' }}>Original Base Fees</td>
                  <td style={{ textAlign: 'right' }}>{convertToRupees(discountInfo.basePremium)}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: '600' }}>Discount Amount</td>
                  <td style={{ textAlign: 'right', color: '#28a745', fontWeight: 'bold' }}>
                    -{convertToRupees(discountInfo.amount)}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: '600' }}>Final Base Fees</td>
                  <td style={{ textAlign: 'right', color: '#00239c', fontWeight: 'bold' }}>
                    {convertToRupees(discountInfo.finalBaseAmount)}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: '600' }}>Add-ons Amount</td>
                  <td style={{ textAlign: 'right' }}>{convertToRupees(liveTotalAddonCost)}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: '600' }}>Total Amount</td>
                  <td style={{ textAlign: 'right' }}>{convertToRupees(liveDiscountTotalAmount)}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: '600' }}>GST (18%)</td>
                  <td style={{ textAlign: 'right' }}>{convertToRupees(liveDiscountTaxAmount)}</td>
                </tr>
                <tr style={{ borderTop: '2px solid #c3e6cb' }}>
                  <td style={{ fontWeight: '700' }}>Final Payable Amount</td>
                  <td style={{ textAlign: 'right', color: '#00239c', fontWeight: 'bold', fontSize: '12px' }}>
                    {convertToRupees(liveDiscountFinalPayable)}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: '600' }}>Commission Amount</td>
                  <td style={{ textAlign: 'right' }}>{convertToRupees(discountInfo.commissionAmount)}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: '600' }}>MSC Amount</td>
                  <td style={{ textAlign: 'right' }}>{convertToRupees(discountInfo.mscAmount)}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: '600' }}>Final MSC %</td>
                  <td style={{ textAlign: 'right' }}>{discountInfo.finalMscPercentage?.toFixed(2)}%</td>
                </tr>
              </tbody>
            </Table>
          </>
        )}

        {/* Already-Selected Addons */}
        {existingServices.length > 0 && (
          <>
            <h6 style={{ color: '#00239c', marginTop: '12px', marginBottom: '8px', fontSize: '12px' }}>
              <strong>Selected Add-ons</strong>
            </h6>
            <div style={{ marginBottom: '16px' }}>
              {existingServices.map((svc, idx) => {
                const addonCostAmt = calculateAddonCost(svc, planCostForAddon);
                return (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '6px 10px',
                      marginBottom: '6px',
                      backgroundColor: '#f0f4ff',
                      borderRadius: '4px',
                      border: '1px solid #c5cae9',
                      fontSize: '11px',
                    }}
                  >
                    <div>
                      <Badge
                        color={svc.add_on_type?.toLowerCase() === 'assistance' ? 'info' : 'warning'}
                        style={{ fontSize: '9px', marginRight: '6px' }}
                      >
                        {svc.add_on_type || 'Addon'}
                      </Badge>
                      <span style={{ fontWeight: '600' }}>{svc.label}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ color: '#00239c', fontWeight: 'bold' }}>
                        {convertToRupees(addonCostAmt)}
                      </span>
                      <Button
                        color="danger"
                        size="sm"
                        outline
                        onClick={() => handleRemoveAddon(svc, recordId)}
                        style={{ padding: '1px 6px', fontSize: '10px', lineHeight: '1.4' }}
                      >
                        ✕
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Addon Dropdowns Section */}
        <h6 style={{ color: '#333', marginTop: '12px', marginBottom: '8px', fontSize: '12px' }}>
          <strong>Service Add-ons</strong>
        </h6>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spinner size="sm" color="primary" />
            <span style={{ marginLeft: '8px', fontSize: '11px', color: '#666' }}>Loading available add-ons...</span>
          </div>
        ) : (
          <div style={{ backgroundColor: '#fafafa', borderRadius: '6px', padding: '12px', border: '1px solid #e0e0e0' }}>
            {/* Assistance Addon Dropdown */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#333', marginBottom: '6px' }}>
                Assistance Add-on:
              </label>
              {assistanceAddons.length > 0 ? (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <Select
                      options={formatAddonOptions(assistanceAddons)}
                      value={localAssistanceAddon}
                      onChange={handleAssistanceSelect}
                      placeholder="-- Select Assistance Add-on --"
                      isClearable
                      styles={selectStyles}
                    />
                  </div>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={handleAddAssistance}
                    disabled={!localAssistanceAddon}
                    style={{ whiteSpace: 'nowrap', fontSize: '11px', padding: '5px 12px' }}
                  >
                    + Add
                  </Button>
                </div>
              ) : (
                <p style={{ fontSize: '11px', color: '#999', margin: '0' }}>No assistance add-ons available for this plan.</p>
              )}
              {localAssistanceAddon && (
                <div style={{ marginTop: '6px', fontSize: '11px', color: '#555', backgroundColor: '#e8f5e9', padding: '6px 10px', borderRadius: '4px' }}>
                  <strong>Cost:</strong>{' '}
                  <span style={{ color: '#00239c', fontWeight: 'bold' }}>
                    {convertToRupees(calculateAddonCost(localAssistanceAddon, planCostForAddon))}
                  </span>
                  {localAssistanceAddon.addon_value_type?.toLowerCase() !== 'fixed' && (
                    <span style={{ color: '#666', marginLeft: '6px' }}>
                      ({localAssistanceAddon.addon_coa_value}% of plan cost)
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Insurance Addon Dropdown */}
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#333', marginBottom: '6px' }}>
                Insurance Add-on:
              </label>
              {insuranceAddons.length > 0 ? (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <Select
                      options={formatAddonOptions(
                        insuranceAddons.filter(
                          (a) => !existingServices.some((s) => s.add_on_type?.toLowerCase() === 'insurance' && s.value === a.value)
                        )
                      )}
                      value={localInsuranceAddon}
                      onChange={handleInsuranceSelect}
                      placeholder="-- Select Insurance Add-on --"
                      isClearable
                      styles={selectStyles}
                      isDisabled={existingServices.some((s) => s.add_on_type?.toLowerCase() === 'insurance')}
                    />
                  </div>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={handleAddInsurance}
                    disabled={!localInsuranceAddon || existingServices.some((s) => s.add_on_type?.toLowerCase() === 'insurance')}
                    style={{ whiteSpace: 'nowrap', fontSize: '11px', padding: '5px 12px' }}
                  >
                    + Add
                  </Button>
                </div>
              ) : (
                <p style={{ fontSize: '11px', color: '#999', margin: '0' }}>No insurance add-ons available for this plan.</p>
              )}
              {existingServices.some((s) => s.add_on_type?.toLowerCase() === 'insurance') && (
                <small style={{ fontSize: '10px', color: '#856404' }}>
                  Only one insurance add-on allowed per record. Remove the existing one to change.
                </small>
              )}
              {localInsuranceAddon && (
                <div style={{ marginTop: '6px', fontSize: '11px', color: '#555', backgroundColor: '#e8f5e9', padding: '6px 10px', borderRadius: '4px' }}>
                  <strong>Cost:</strong>{' '}
                  <span style={{ color: '#00239c', fontWeight: 'bold' }}>
                    {convertToRupees(calculateAddonCost(localInsuranceAddon, planCostForAddon))}
                  </span>
                  {localInsuranceAddon.addon_value_type?.toLowerCase() !== 'fixed' && (
                    <span style={{ color: '#666', marginLeft: '6px' }}>
                      ({localInsuranceAddon.addon_coa_value}% of plan cost)
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Live Pricing Summary */}
        <div
          style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#e8eaf6',
            borderRadius: '6px',
            border: '1px solid #c5cae9',
          }}
        >
          <h6 style={{ color: '#00239c', marginBottom: '8px', fontSize: '12px' }}>
            <strong>Live Pricing Summary</strong>
          </h6>
          <Table borderless size="sm" style={{ fontSize: '11px', marginBottom: '0' }}>
            <tbody>
              <tr>
                <td style={{ fontWeight: '600', width: '55%' }}>Base Fees (after discount)</td>
                <td style={{ textAlign: 'right' }}>{convertToRupees(finalBasePremium)}</td>
              </tr>
              {recordAddonCost > 0 && (
                <tr>
                  <td style={{ fontWeight: '600' }}>Existing Add-ons</td>
                  <td style={{ textAlign: 'right' }}>{convertToRupees(recordAddonCost)}</td>
                </tr>
              )}
              {assistanceCost > 0 && (
                <tr>
                  <td style={{ fontWeight: '600', color: '#1565c0' }}>New Assistance Add-on</td>
                  <td style={{ textAlign: 'right', color: '#1565c0' }}>+ {convertToRupees(assistanceCost)}</td>
                </tr>
              )}
              {insuranceCost > 0 && (
                <tr>
                  <td style={{ fontWeight: '600', color: '#1565c0' }}>New Insurance Add-on</td>
                  <td style={{ textAlign: 'right', color: '#1565c0' }}>+ {convertToRupees(insuranceCost)}</td>
                </tr>
              )}
              <tr style={{ borderTop: '1px solid #c5cae9' }}>
                <td style={{ fontWeight: '600' }}>Subtotal</td>
                <td style={{ textAlign: 'right' }}>{convertToRupees(liveBasePlusAddon)}</td>
              </tr>
              <tr>
                <td style={{ fontWeight: '600' }}>GST (18%)</td>
                <td style={{ textAlign: 'right' }}>{convertToRupees(liveGst)}</td>
              </tr>
              <tr style={{ borderTop: '2px solid #9fa8da' }}>
                <td style={{ fontWeight: '700', color: '#00239c' }}>Total Payable</td>
                <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#00239c', fontSize: '12px' }}>
                  {convertToRupees(liveMemberPremium)}
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button color="primary" size="sm" onClick={toggle} style={{ fontSize: '12px', padding: '5px 20px' }}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default RecordDetailsModal;
